import { useState } from 'react';
import { useTeam, useInviteMember, useSetMemberRole, useRemoveMember } from '../hooks/useTeam';
import { useAuth } from '../hooks/useAuth';
import { ROLE_OPTIONS, type Role } from '../lib/permissions';
import type { TeamMember } from '../types/team';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function Team() {
  const { data: team, isLoading, error } = useTeam();
  const { user } = useAuth();
  const [showInvite, setShowInvite] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-sm text-gray-500">
            Internal SecureLink staff and their roles.
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} className="btn-primary">
          Invite Teammate
        </button>
      </div>

      {isLoading && <LoadingSpinner />}
      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {(error as Error).message}
        </div>
      )}

      {team && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {team.map((m) => (
                <TeamRow key={m.username} member={m} currentEmail={user?.email ?? ''} />
              ))}
              {team.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                    No team members yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function TeamRow({ member, currentEmail }: { member: TeamMember; currentEmail: string }) {
  const setRole = useSetMemberRole();
  const removeMember = useRemoveMember();
  const isSelf = (member.email ?? '').toLowerCase() === currentEmail.toLowerCase();
  const currentGroup = (member.groups[0] as Role) ?? 'support';

  const statusColor =
    member.status === 'CONFIRMED'
      ? 'bg-green-100 text-green-800'
      : member.status === 'FORCE_CHANGE_PASSWORD'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-100 text-gray-800';
  const statusLabel =
    member.status === 'FORCE_CHANGE_PASSWORD' ? 'Invited' : member.status ?? 'Unknown';

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.name || '—'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{member.email}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        <select
          className="input py-1"
          value={currentGroup}
          disabled={setRole.isPending}
          onChange={(e) =>
            setRole.mutate({ username: member.username, role: e.target.value })
          }
        >
          {ROLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColor}`}>
          {statusLabel}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
        {isSelf ? (
          <span className="text-gray-400">You</span>
        ) : (
          <button
            className="text-red-600 hover:text-red-800 disabled:opacity-50"
            disabled={removeMember.isPending}
            onClick={() => {
              if (confirm(`Remove ${member.email} from the team?`)) {
                removeMember.mutate(member.username);
              }
            }}
          >
            Remove
          </button>
        )}
      </td>
    </tr>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const invite = useInviteMember();
  const [formData, setFormData] = useState({ name: '', email: '', role: 'sales-engineers' as Role });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await invite.mutateAsync(formData);
      onClose();
    } catch {
      // surfaced below via invite.error
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Invite Teammate</h2>
        <p className="text-sm text-gray-500 mb-4">
          They'll receive an email invitation with a temporary password to set their own.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as Role })}
              className="input"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {invite.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {(invite.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
              Cancel
            </button>
            <button type="submit" disabled={invite.isPending} className="btn-primary">
              {invite.isPending ? 'Inviting…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
